import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class StarVariantMatrix extends HTMLElement {
  #eventController = null;
  #submitting = false;
  #selectionRevision = 0;

  connectedCallback() {
    this.#eventController?.abort();
    this.#eventController = new AbortController();
    const { signal } = this.#eventController;

    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    const form = this.querySelector('.star-variant-matrix__form');
    form?.addEventListener('input', this.#handleQuantityInput, { signal });
    form?.addEventListener('change', this.#handleQuantityInput, { signal });
    form?.addEventListener('submit', this.#handleSubmit, { signal });

    this.#syncSubmitState();
  }

  disconnectedCallback() {
    this.#eventController?.abort();
    this.#eventController = null;
  }

  #handleQuantityInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('.star-variant-matrix__quantity')) return;

    const row = event.target.closest('.star-variant-matrix__row');
    const rowStatus = row?.querySelector('.star-variant-matrix__row-status');
    if (rowStatus instanceof HTMLElement) {
      rowStatus.textContent = '';
      delete rowStatus.dataset.success;
    }

    this.#validateInput(event.target);
    this.#syncSubmitState();
  };

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const inputs = this.#getInputs();
    if (inputs.length === 0) return;

    this.#setSubmitting(true);
    let deferredEventPromise = null;
    let cartBeforeAdd = null;
    let submittedEntries = [];
    let itemCount = 0;
    let mutationSucceeded = false;
    let didSucceed = false;
    let didFail = false;

    try {
      cartBeforeAdd = await this.#fetchCart();
      const cartQuantities = this.#getCartQuantities(cartBeforeAdd);
      const validation = this.#collectEntries(inputs, cartQuantities);

      if (validation.invalidInput) {
        validation.invalidInput.focus({ preventScroll: true });
        validation.invalidInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.#setStatus(this.dataset.reviewMessage ?? '', 'error');
        return;
      }

      if (validation.entries.length === 0) {
        this.#setStatus(this.dataset.emptyMessage ?? '', 'neutral');
        return;
      }

      submittedEntries = validation.entries;
      const lines = submittedEntries.map(({ variantId, quantity }) => ({
        merchandiseId: variantId,
        quantity,
      }));

      deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines,
          promise: deferredEventPromise.promise,
          detail: {
            source: 'star-variant-matrix',
            sourceId: this.id,
            productId: this.dataset.productId,
          },
        })
      );

      const addResponse = await fetch(this.dataset.cartAddUrl ?? '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: submittedEntries.map(({ variantId, quantity }) => ({ id: variantId, quantity })),
        }),
      });
      const addResult = await this.#readJson(addResponse);

      if (!addResponse.ok || addResult?.status) {
        const message = addResult?.description || addResult?.message || this.dataset.errorMessage || '';
        const error = new Error(message);
        error.code = 'INVALID';
        throw error;
      }

      mutationSucceeded = true;
      itemCount = submittedEntries.reduce((sum, entry) => sum + entry.quantity, 0);
      const updatedCart = await this.#fetchCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: {
          items: updatedCart.items,
          source: 'star-variant-matrix',
          sourceId: this.id,
          itemCount,
          productId: this.dataset.productId,
          didError: false,
        },
      });

      this.#showSuccess(submittedEntries, itemCount);
      didSucceed = true;
    } catch (error) {
      if (mutationSucceeded && deferredEventPromise) {
        deferredEventPromise.resolve({
          cart: null,
          detail: {
            source: 'star-variant-matrix',
            sourceId: this.id,
            itemCount,
            productId: this.dataset.productId,
            didError: false,
          },
        });
        this.#showSuccess(submittedEntries, itemCount);
        didSucceed = true;
        return;
      }

      const message = error instanceof Error && error.message ? error.message : this.dataset.errorMessage ?? '';

      if (deferredEventPromise && cartBeforeAdd) {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartBeforeAdd),
          detail: {
            items: cartBeforeAdd.items,
            source: 'star-variant-matrix',
            sourceId: this.id,
            itemCount: 0,
            productId: this.dataset.productId,
            didError: true,
          },
        });
      } else {
        deferredEventPromise?.reject(error);
      }

      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: error?.code === 'INVALID' ? 'INVALID' : 'SERVICE_UNAVAILABLE',
          detail: {
            source: 'star-variant-matrix',
            sourceId: this.id,
          },
        })
      );
      this.#setStatus(message, 'error');
      didFail = true;
    } finally {
      this.#setSubmitting(false, !didSucceed && !didFail);
    }
  };

  #collectEntries(inputs, cartQuantities) {
    const entries = [];
    let invalidInput = null;

    for (const input of inputs) {
      const row = input.closest('.star-variant-matrix__row');
      if (!(row instanceof HTMLElement) || row.dataset.orderable !== 'true') continue;

      const variantId = row.dataset.variantId ?? '';
      const cartQuantity = cartQuantities.get(variantId) ?? 0;
      const result = this.#validateInput(input, cartQuantity);

      if (result.error && !invalidInput) invalidInput = input;
      if (!result.error && result.quantity > 0) {
        entries.push({ input, row, variantId, quantity: result.quantity });
      }
    }

    return { entries, invalidInput };
  }

  #validateInput(input, cartQuantity = null) {
    const row = input.closest('.star-variant-matrix__row');
    if (!(row instanceof HTMLElement)) return { quantity: 0, error: '' };

    const value = input.value.trim();
    if (value === '') {
      this.#setInputError(input, '');
      return { quantity: 0, error: '' };
    }

    const quantity = Number(value);
    const minimum = this.#positiveInteger(row.dataset.minimum, 1);
    const increment = this.#positiveInteger(row.dataset.increment, 1);
    const maximum = this.#optionalPositiveInteger(row.dataset.maximum);
    let error = '';

    if (!Number.isSafeInteger(quantity) || quantity < minimum || (quantity - minimum) % increment !== 0) {
      error = this.#format(this.dataset.quantityErrorTemplate, {
        __RULES__: row.querySelector('.star-variant-matrix__rules')?.textContent?.trim() ?? '',
      });
    } else if (maximum !== null && cartQuantity === null && quantity > maximum) {
      error = this.#format(this.dataset.quantityErrorTemplate, {
        __RULES__: row.querySelector('.star-variant-matrix__rules')?.textContent?.trim() ?? '',
      });
    } else if (maximum !== null && cartQuantity !== null && cartQuantity + quantity > maximum) {
      error = this.#format(this.dataset.quantityMaxErrorTemplate, {
        __CART__: String(cartQuantity),
        __MAXIMUM__: String(maximum),
      });
    }

    this.#setInputError(input, error);
    return { quantity: Number.isSafeInteger(quantity) ? quantity : 0, error };
  }

  #setInputError(input, message) {
    const rowStatus = input.closest('.star-variant-matrix__row')?.querySelector('.star-variant-matrix__row-status');
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
    if (rowStatus instanceof HTMLElement) rowStatus.textContent = message;
  }

  #syncSubmitState() {
    const submit = this.querySelector('.star-variant-matrix__submit');
    if (!(submit instanceof HTMLButtonElement)) return;

    let selectedCount = 0;
    let hasError = false;

    for (const input of this.#getInputs()) {
      const result = this.#validateInput(input);
      if (result.quantity > 0) selectedCount += 1;
      if (result.error) hasError = true;
    }

    submit.disabled = this.#submitting || selectedCount === 0 || hasError;

    if (this.#submitting) return;
    if (hasError) {
      this.#setStatus(this.dataset.reviewMessage ?? '', 'error');
    } else if (selectedCount > 0) {
      this.#setStatus(
        this.#format(this.dataset.selectedTemplate, { __COUNT__: String(selectedCount) }),
        'neutral'
      );
    } else {
      this.#setStatus(this.dataset.emptyMessage ?? '', 'neutral');
    }
  }

  #setSubmitting(submitting, synchronize = true) {
    this.#submitting = submitting;
    const submit = this.querySelector('.star-variant-matrix__submit');
    const form = this.querySelector('.star-variant-matrix__form');

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = submitting || submit.disabled;
      submit.textContent = submitting ? this.dataset.addingLabel ?? '' : this.dataset.addLabel ?? '';
    }
    if (form instanceof HTMLFormElement) form.setAttribute('aria-busy', String(submitting));

    if (!submitting && synchronize) {
      this.#syncSubmitState();
    } else if (!submitting && submit instanceof HTMLButtonElement) {
      const inputs = this.#getInputs();
      const hasSelection = inputs.some((input) => input.value.trim() !== '');
      const hasInputError = inputs.some((input) => input.getAttribute('aria-invalid') === 'true');
      submit.disabled = !hasSelection || hasInputError;
    }
  }

  #showSuccess(entries, itemCount) {
    for (const { input, row } of entries) {
      input.value = '';
      input.removeAttribute('aria-invalid');
      const rowStatus = row.querySelector('.star-variant-matrix__row-status');
      if (rowStatus instanceof HTMLElement) {
        rowStatus.textContent = this.dataset.addedLabel ?? '';
        rowStatus.dataset.success = 'true';
      }
    }

    const cartLink = this.querySelector('.star-variant-matrix__cart-link');
    if (cartLink instanceof HTMLElement) cartLink.hidden = false;

    this.#setStatus(
      this.#format(this.dataset.addedTemplate, {
        __LINES__: String(entries.length),
        __QUANTITY__: String(itemCount),
      }),
      'success'
    );
  }

  #setStatus(message, state) {
    const status = this.querySelector('.star-variant-matrix__status');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.dataset.state = state;
  }

  #getInputs() {
    return Array.from(this.querySelectorAll('.star-variant-matrix__quantity')).filter(
      (input) => input instanceof HTMLInputElement && !input.disabled
    );
  }

  #getCartQuantities(cart) {
    const quantities = new Map();
    for (const item of cart?.items ?? []) {
      const variantId = String(item.variant_id ?? item.id ?? '');
      quantities.set(variantId, (quantities.get(variantId) ?? 0) + Number(item.quantity ?? 0));
    }
    return quantities;
  }

  async #fetchCart() {
    const response = await fetch(this.dataset.cartJsonUrl ?? '/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const cart = await this.#readJson(response);
    if (!response.ok) throw new Error(cart?.description || cart?.message || this.dataset.errorMessage || '');
    return cart;
  }

  async #readJson(response) {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(this.dataset.errorMessage ?? '');
    }
  }

  #positiveInteger(value, fallback) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  #optionalPositiveInteger(value) {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  #format(template, replacements) {
    let output = template ?? '';
    for (const [token, value] of Object.entries(replacements)) output = output.replaceAll(token, value);
    return output;
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || !('promise' in event)) return;
    if (event.target.closest('product-card, [id*="QuickAdd-"], quick-add-component, dialog')) return;
    if (this.#submitting) return;

    const productId = this.dataset.productId ?? '';
    const sectionId = this.dataset.sectionId ?? '';
    if (!sectionId) return;
    const selectionRevision = ++this.#selectionRevision;

    Promise.resolve(event.promise)
      .then((result) => {
        if (!this.isConnected) return;
        if (this.#submitting || selectionRevision !== this.#selectionRevision) return;

        const detail = result?.detail;
        const html = detail?.html;
        if (!(html instanceof Document)) return;

        const eventProductId = String(detail.productId ?? '');
        const loadsNewProduct = Boolean(detail.newProduct);
        if (!loadsNewProduct && eventProductId && eventProductId !== productId) return;

        const selector = `star-variant-matrix[data-section-id="${CSS.escape(sectionId)}"]`;
        const newMatrix = html.querySelector(selector);
        if (!(newMatrix instanceof HTMLElement)) return;

        const newProductId = newMatrix.dataset.productId ?? '';
        if (!loadsNewProduct && eventProductId && newProductId !== eventProductId) return;

        const retainedQuantities = new Map(
          this.#getInputs()
            .filter((input) => input.value.trim() !== '')
            .map((input) => [input.closest('.star-variant-matrix__row')?.dataset.variantId ?? '', input.value])
        );
        const wasOpen = Boolean(this.querySelector('.star-variant-matrix__details')?.open);
        const newDetails = newMatrix.querySelector('.star-variant-matrix__details');
        if (newDetails instanceof HTMLDetailsElement && wasOpen) newDetails.open = true;

        for (const input of newMatrix.querySelectorAll('.star-variant-matrix__quantity')) {
          if (!(input instanceof HTMLInputElement)) continue;
          const variantId = input.closest('.star-variant-matrix__row')?.dataset.variantId ?? '';
          const retainedValue = retainedQuantities.get(variantId);
          if (retainedValue !== undefined) input.value = retainedValue;
        }

        this.replaceWith(newMatrix);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[star-variant-matrix] Variant update failed:', error);
      });
  };
}

if (!customElements.get('star-variant-matrix')) {
  customElements.define('star-variant-matrix', StarVariantMatrix);
}
